import api from '../index'

// 路由权限配置接口
export interface RoutePermission {
  id?: number
  route_id: number
  permission_code: string
  permission_name: string
  permission_type: 'button' | 'api' | 'menu'
  status: number
  created_at?: string
  updated_at?: string
}

// 路由配置接口（前置声明）
export interface RouteConfig {
  id?: number
  name: string
  title: string
  path: string
  component?: string
  icon?: string
  type: number // 1:目录 2:菜单 3:按钮
  platform: string
  module_code?: string
  parent_id: number
  level: number
  sort_order: number
  status: number
  permission_code?: string
  is_hidden?: number
  keep_alive?: number
  external_link?: string
  meta?: Record<string, any>
  remark?: string
  children?: RouteConfig[] // 子路由
  permissions?: RoutePermission[] // 路由关联的权限列表
  hasChildren?: boolean // 是否有子路由
  created_at?: string
  updated_at?: string
}

// 系统模块数据类型定义
export interface SystemModule {
  id: number
  name: string
  code: string
  description?: string
  status: 0 | 1 // 0=禁用，1=启用
  sort_order: number
  config: Record<string, any> // JSON配置对象
  version?: string
  author?: string
  dependencies: string[] // 依赖模块代码数组
  created_by: number
  updated_by?: number
  created_at: string
  updated_at: string
  creator?: {
    id: number
    username: string
    name: string
  }
  updater?: {
    id: number
    username: string
    name: string
  }
}

// 模块依赖关系
export interface ModuleDependency {
  id: number
  name: string
  code: string
  status: 0 | 1
  dependencies: string[]
  dependents: Record<string, string> // 依赖此模块的其他模块
}

// 模块搜索参数
export interface ModuleSearchParams {
  name?: string
  code?: string
  status?: number
  per_page?: number
  page?: number
}

// 模块创建/更新参数
export interface ModuleFormData {
  name: string
  code: string
  description?: string
  status?: boolean
  sort_order?: number
  config?: Record<string, any>
  version?: string
  author?: string
  dependencies?: string[]
}

// 模块配置参数
export interface ModuleConfigParams {
  code: string
  config: Record<string, any>
}

// 批量排序参数
export interface BatchSortParams {
  sort_data: Array<{
    id: number
    sort_order: number
  }>
}

// 系统模块管理API
export const systemModuleAPI = {
  /**
   * 获取模块列表
   */
  getList(params?: ModuleSearchParams) {
    return api.get('system-modules', { params })
  },

  /**
   * 获取启用的模块列表
   */
  getEnabledList() {
    return api.get('system-modules/enabled')
  },

  /**
   * 获取模块依赖关系图
   */
  getDependencyGraph() {
    return api.get('system-modules/dependency-graph')
  },

  /**
   * 获取模块配置
   */
  getConfig(code: string) {
    return api.get('system-modules/config', {
      params: { code },
    })
  },

  /**
   * 更新模块配置
   */
  updateConfig(data: ModuleConfigParams) {
    return api.put('system-modules/config', data)
  },

  /**
   * 创建模块
   */
  create(data: ModuleFormData) {
    return api.post('system-modules', data)
  },

  /**
   * 获取模块详情
   */
  getDetail(id: number) {
    return api.get(`system-modules/${id}`)
  },

  /**
   * 根据代码获取模块详情
   */
  getByCode(code: string) {
    return api.get(`system-modules/code/${code}`)
  },

  /**
   * 更新模块
   */
  update(id: number, data: Partial<ModuleFormData>) {
    return api.put(`system-modules/${id}`, data)
  },

  /**
   * 删除模块
   */
  delete(id: number) {
    return api.delete(`system-modules/${id}`)
  },

  /**
   * 切换模块状态
   */
  toggleStatus(id: number) {
    return api.patch(`system-modules/${id}/toggle`)
  },

  /**
   * 批量更新排序
   */
  batchUpdateSort(data: BatchSortParams) {
    return api.patch('system-modules/batch/sort', data)
  },

  /**
   * 获取模块路由配置
   */
  getRoutes(params: { code: string, platform?: string }) {
    return api.get('system-modules/routes', { params })
  },

  /**
   * 更新模块路由配置
   */
  updateRoutes(data: {
    code: string
    platform: string
    routes: RouteConfig[]
  }) {
    return api.put('admin/system-modules/routes', data)
  },

  /**
   * 验证路由配置
   */
  validateRoutes(data: {
    routes: RouteConfig[]
    platform?: string
  }) {
    return api.post('admin/system-modules/routes/validate', data)
  },

  /**
   * 批量操作路由
   */
  batchRoutes(data: {
    code: string
    platform: string
    action: 'enable' | 'disable' | 'delete'
    route_ids: number[]
  }) {
    return api.patch('admin/system-modules/routes/batch', data)
  },

  /**
   * 创建单个路由
   */
  createRoute(data: {
    code: string
    platform: string
    route: Partial<RouteConfig>
  }) {
    return api.post('admin/system-modules/routes/single', data)
  },

  /**
   * 更新单个路由
   */
  updateRoute(data: {
    code: string
    platform: string
    routeId: number
    route: Partial<RouteConfig>
  }) {
    return api.put('admin/system-modules/routes/single', data)
  },

  /**
   * 删除单个路由
   */
  deleteRoute(data: {
    code: string
    platform: string
    routeId: number
  }) {
    return api.delete('admin/system-modules/routes/single', { data })
  },

  /**
   * 获取路由统计信息
   */
  getRouteStats(params: { code: string, platform?: string }) {
    return api.get('admin/system-modules/routes/stats', { params })
  },

}

// 模块状态映射
export const ModuleStatusMap = {
  0: { text: '禁用', color: 'danger' },
  1: { text: '启用', color: 'success' },
} as const

// 工具函数
export const moduleUtils = {
  /**
   * 获取模块状态文本
   */
  getStatusText(status: number): string {
    return ModuleStatusMap[status as keyof typeof ModuleStatusMap]?.text || '未知'
  },

  /**
   * 获取模块状态颜色
   */
  getStatusColor(status: number): 'success' | 'primary' | 'warning' | 'info' | 'danger' {
    return ModuleStatusMap[status as keyof typeof ModuleStatusMap]?.color || 'info'
  },

  /**
   * 验证模块代码格式
   */
  validateCode(code: string): boolean {
    return /^\w+$/.test(code)
  },

  /**
   * 格式化依赖关系显示
   */
  formatDependencies(dependencies: string[]): string {
    return dependencies.length > 0 ? dependencies.join(', ') : '无依赖'
  },

  /**
   * 检查模块是否可以禁用（没有其他模块依赖它）
   */
  canDisable(module: SystemModule, allModules: SystemModule[]): boolean {
    return !allModules.some(m =>
      m.id !== module.id
      && m.status === 1
      && m.dependencies.includes(module.code),
    )
  },

  /**
   * 获取模块的依赖模块
   */
  getDependentModules(moduleCode: string, allModules: SystemModule[]): SystemModule[] {
    return allModules.filter(m => m.dependencies.includes(moduleCode))
  },

  /**
   * 构建依赖关系树
   */
  buildDependencyTree(modules: SystemModule[]): any[] {
    const moduleMap = new Map(modules.map(m => [m.code, m]))

    return modules.map(module => ({
      id: module.id,
      name: module.name,
      code: module.code,
      status: module.status,
      dependencies: module.dependencies.map(dep => ({
        code: dep,
        name: moduleMap.get(dep)?.name || dep,
        status: moduleMap.get(dep)?.status || 0,
      })),
      dependents: this.getDependentModules(module.code, modules).map(dep => ({
        code: dep.code,
        name: dep.name,
        status: dep.status,
      })),
    }))
  },

  /**
   * 验证JSON配置格式
   */
  validateConfig(config: string): { valid: boolean, data?: any, error?: string } {
    try {
      const data = JSON.parse(config)
      return { valid: true, data }
    }
    catch (error) {
      return {
        valid: false,
        error: error instanceof Error ? error.message : '配置格式错误',
      }
    }
  },

  /**
   * 格式化配置显示
   */
  formatConfig(config: Record<string, any>): string {
    return JSON.stringify(config, null, 2)
  },
}

// 默认导出
export default systemModuleAPI
